import React, { useState, useContext, useEffect } from "react";
import GlobalContext from "../../../contexts/globalContext";
import { graphql, useStaticQuery } from "gatsby";
import contentfulRenderOptions from "../../../utils/contentfulRenderOption";

// Styles //
import "./FormUneteComunidad.scss";

// Services //
import { saveForm, getToken } from "../../../services/formServices";
import { saveSocialUserData } from "../../../services/social.services";

// Components //
import Modal from "../../molecules/modal/modal";
import FormHead from "../../molecules/form-head/FormHead";
import FormBody from "../../molecules/form-body/FormBody";
import ProgressBar from "../../atoms/progress-bar/progressBar";
import ModalList from "../../atoms/modal-list/ModalList";
import rawToReact from "../../../utils/rawToReact";

interface IModalContent {
    title: string;
    image: string;
    textButton: string;
    url: string;
}

const FormUneteComunidad = (props: any) => {
    // Obtiene el contenido de un formulario de tipo Contactenos desde contentful y modales
    const data = useStaticQuery(graphql`
        query contentfulFormUneteComunidad {
            form: allContentfulContactenos(
                filter: { contentful_id: { eq: "74l1jtbwYapAnbd76Uz1es" } }
            ) {
                nodes {
                    tituloPrincipal
                    descripcionFormulario {
                        raw
                    }
                    descripcion {
                        raw
                    }
                    metaTitulo
                    tituloFormulario
                    titulosFormulario {
                        tituloGeneral
                        descripcion {
                            raw
                        }
                        referenciaFormulario {
                            tipoCampo
                            listaSelect
                            mensajeError
                            inputPrincipal
                            nombreId
                            tituloBotonTerminos
                            textoTerminos {
                                raw
                            }
                            obligatorio
                            seccion
                        }
                        tituloBoton
                    }
                }
            }
            modalSuccess: allContentfulContactenos(
                filter: { contentful_id: { eq: "6rNhRaqSTcql1JpNC9sUy4" } }
            ) {
                nodes {
                    tituloPrincipal
                    descripcionFormulario {
                        raw
                    }
                    descripcion {
                        raw
                    }
                    metaTitulo
                    imagenFormulario {
                        file {
                            url
                        }
                    }
                    tituloBoton
                    redireccionBoton
                    contentful_id
                }
            }
            modalUnsuccess: allContentfulContactenos(
                filter: { contentful_id: { eq: "1UE2fuZZ7c1NNsC3E3bmUe" } }
            ) {
                nodes {
                    tituloPrincipal
                    descripcionFormulario {
                        raw
                    }
                    descripcion {
                        raw
                    }
                    metaTitulo
                    imagenFormulario {
                        file {
                            url
                        }
                    }
                    tituloBoton
                    redireccionBoton
                    contentful_id
                }
            }
        }
    `);

    const contentfulData = data.form.nodes[0];
    const contentfulModalSuccess = data.modalSuccess.nodes[0];
    const contentfulModalUnsuccess = data.modalUnsuccess.nodes[0];

    const defaultFormValues = {
        fullName: "",
        companyRole: "",
        documentId: "",
        email: "",
        mobile: "",
    };

    const [formValues, setFormValues] = useState(defaultFormValues);
    const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
    const [isError, setIsError] = useState<boolean>(false);
    const [modalContent, setModalContent] = useState<IModalContent>();
    const [inputDisabled, setInputDisabled] = useState<string[]>([]);

    const expectations = [
        "expectation_1",
        "expectation_2",
        "expectation_3",
        "expectation_4",
        "expectation_5",
        "othersExpectations",
    ];

    const tendencies = [
        "tendency_1",
        "tendency_3",
    ]

    const Context = useContext(GlobalContext);

    // expresiones regulares para cada input
    const validationField = (field: string, value: any) => {
        switch (field) {
            case "documentId":
                return (value.length <= 11);

            case "fullName":
                const regexName = /^[ a-zA-ZÀ-ÿ\u00f1\u00d1]*$/g;
                return (regexName.test(value));

            default:
                return true;
        }
    }

    // Validacion para las tendencias
    const tendenciesValidation = (tendency: string, value: boolean) => {
        if (value === false) {
            const auxFormValues = { ...formValues };
            delete auxFormValues[tendency];
            setFormValues(auxFormValues)
            return
        }
        const auxFormValues = { ...formValues };
        auxFormValues[tendency] = "SÍ"
        setFormValues(auxFormValues)
    }

    // Valida y actualiza los valores de los campos diligenciados
    const updateInputValue = (field: string, value: any, type?: string) => {
        if (tendencies.includes(field)) {
            tendenciesValidation(field, value);
        } else if (expectations.includes(field)) {
            if (value) {
                if (typeof value === "boolean") {
                    setFormValues((prev: any) => {
                        return { ...prev, [field]: "SÍ" };
                    });
                } else {
                    setFormValues((prev: any) => {
                        return { ...prev, [field]: value };
                    });
                }
                setFormValues((prev: any) => {
                    return { ...prev, expectations: "SÍ" };
                });
            } else {
                const auxFormValues = { ...formValues };
                delete auxFormValues[field];
                setFormValues(auxFormValues);
                let count = 0;
                expectations.forEach((field) => {
                    if (auxFormValues[field]) {
                        count++;
                    }
                });
                if (count === 0) {
                    delete auxFormValues.expectations;
                    setFormValues(auxFormValues);
                }
            }
        } else if (type && type === "checkbox") {
            if (value) {
                setFormValues((prev: any) => {
                    return { ...prev, [field]: "SÍ" };
                });
            } else {
                const auxFormValues = { ...formValues };
                delete auxFormValues[field];
                setFormValues(auxFormValues);
            }
        } else {
            if (validationField(field, value)) {
                setFormValues((prev: any) => {
                    return { ...prev, [field]: value };
                });
            }
        }
    };

    // Guarda en un estado con el contenido de los modales que se renderizan al finalizar el formulario
    const handleModalContent = (status: number) => {
        if (status === 200) {
            setModalContent({
                title: contentfulModalSuccess.tituloPrincipal,
                image: contentfulModalSuccess.imagenFormulario.file.url,
                textButton: contentfulModalSuccess.tituloBoton,
                url: contentfulModalSuccess.redireccionBoton,
            });
            setIsError(false);
        } else {
            setModalContent({
                title: contentfulModalUnsuccess.tituloPrincipal,
                image: contentfulModalUnsuccess.imagenFormulario.file.url,
                textButton: contentfulModalUnsuccess.tituloBoton,
                url: contentfulModalUnsuccess.redireccionBoton,
            });
            setIsError(true);
        }
    };

    const handleSubmitForm = async () => {
        const payload: any = {
            idForm: 1,
            answers: [],
        };

        Object.entries(formValues).forEach(([key, value]) => {
            payload.answers.push({ contentfulId: key, answer: value });
        });

        tendencies.forEach((tendency) => { if (!formValues[tendency]) payload.answers.push({ contentfulId: tendency, answer: 'NO' }); })

        const token = await getToken(Context?.gigyaContext?.gigyaAccount?.UID);

        const response = await saveForm(payload, token);
        if (response.data.status === 200) {
            const userData = {
                "cedula": Context.gigyaContext.gigyaAccount!.data.docNumber,
                "email": Context.gigyaContext.gigyaAccount!.profile.email,
                "name": Context.gigyaContext.gigyaAccount!.profile.firstName + " " + Context.gigyaContext.gigyaAccount!.profile.lastName,
                "photo": Context.gigyaContext.gigyaAccount?.profile.photoURL
            };
            saveSocialUserData(token, userData);
        }
        handleModalContent(response.data.status);
        setIsOpenModal(true);
    };


    // Retorna el contenido de un modal dependiendo del resultado al completar un formulario
    const getContentModal = () => {
        // Si hay error al completar el formulario se renderiza el contenido del modal no exitoso
        if (isError) {
            return (
                <p>
                    {rawToReact(
                        contentfulModalUnsuccess.descripcionFormulario.raw,
                        contentfulRenderOptions
                    )}
                </p>
            );
        } else {
            // Si se completa el formulario se renderiza el contenido del modal exitoso
            return (
                <ModalList
                    textList={JSON.parse(contentfulModalSuccess.descripcionFormulario.raw).content[0].content[0].value.split(
                        "\n"
                    )}
                />
            );
        }
    };

    // cuando se renderiza el grupo todos los inputs obligatorios son agregados a una lista
    const createInputGroupRequiredList = () => {
        const inputList: any = [];
        contentfulData.titulosFormulario.map((tituloFormulario) => {
            tituloFormulario.referenciaFormulario.map((input) => {
                if (input.obligatorio) {
                    inputList.push(input.nombreId);
                }
            });
        });
        return inputList;
    };

    // Guarda los valores de la cuente de gigya que este logueada
    useEffect(() => {
        if (!Context.gigyaContext.gigyaAccount) {
            const timer = setTimeout(() => {
                if (!Context.gigyaContext.gigyaAccount) {
                    Context.gigyaContext.loginComfamaId!();
                    setInputDisabled([]);
                }
            }, 2500);
            return () => clearTimeout(timer);
        } else {
            setFormValues({
                ...formValues,
                fullName:
                    Context.gigyaContext.gigyaAccount.profile.firstName +
                    " " +
                    Context.gigyaContext.gigyaAccount.profile.lastName,
                documentId: Context.gigyaContext.gigyaAccount.data.docNumber,
                email: Context.gigyaContext.gigyaAccount.profile.email,
                mobile: Context.gigyaContext.gigyaAccount.data.mobile,
            });
            setInputDisabled(["documentId"]);
        }
    }, [Context.gigyaContext.gigyaAccount]);


    useEffect(() => {
        // Deshabilitar el scroll
        isOpenModal ? document.body.style.overflow = 'hidden' : document.body.style.overflow = 'auto';
    }, [isOpenModal]);

    return (
        <>
            {Context.gigyaContext.gigyaAccount && (
                <div
                    className={`o-form-unete-comunidad ${Context.gigyaContext.gigyaAccount
                        ? "--disable-input"
                        : ""
                        }`}
                >
                    {/* Renderiza el encabezado del formulario, como titulos y descripciones */}
                    <FormHead
                        tituloPrincipal={contentfulData.tituloPrincipal}
                        descripcion={contentfulData.descripcion}
                        tituloFormulario={contentfulData.tituloFormulario}
                        descripcionFormulario={
                            JSON.parse(contentfulData.descripcionFormulario.raw)
                        }
                    ></FormHead>

                    {/* Renderiza el cuerpo del formulario */}
                    <FormBody
                        // Campos y estilos del formulario
                        titulosFormulario={contentfulData.titulosFormulario}
                        genericClassNameForm={contentfulData.metaTitulo}
                        setInputValue={updateInputValue}
                        formValues={formValues}
                        handleSubmitForm={handleSubmitForm}
                        inputDisabled={inputDisabled}
                    ></FormBody>

                    {/* Renderiza la barra de progreso del formulario */}
                    <ProgressBar
                        formValues={formValues}
                        requiredValues={createInputGroupRequiredList()}
                    ></ProgressBar>

                    {/* Renderiza el modal */}
                    {isOpenModal && (
                        <Modal
                            image={
                                modalContent?.image ? modalContent.image : ""
                            }
                            title={
                                modalContent?.title ? modalContent.title : ""
                            }
                            textButton={
                                modalContent?.textButton
                                    ? modalContent.textButton
                                    : "VOLVER"
                            }
                            url={modalContent?.url ? modalContent.url : ""}
                        >
                            <div
                                className={`o-form-${contentfulData.metaTitulo}__content-modal`}
                            >
                                {getContentModal()}
                            </div>
                        </Modal>
                    )}
                </div>
            )}
        </>
    );
};

export default FormUneteComunidad;
